import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Zap, Clock3, Shield, Users, Target, Crosshair } from "lucide-react";
import Navbar from "../components/Navbar";
import PageNavCard from "../components/PageNavCard";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function minToNumber(m) {
  if (m === null || m === undefined || m === "") return 0;
  if (typeof m === "number") return m;
  const s = String(m);
  if (s.includes(":")) {
    const [mm, ss] = s.split(":");
    return Number(mm || 0) + Number(ss || 0) / 60;
  }
  return Number(s) || 0;
}

const STATS = [
  { key: "pts",    label: "PPG",  season: "pts",    recent: "recent_pts",    isPct: false, isMin: false },
  { key: "min",    label: "MIN",  season: "min",    recent: "recent_min",    isPct: false, isMin: true  },
  { key: "reb",    label: "RPG",  season: "reb",    recent: "recent_reb",    isPct: false, isMin: false },
  { key: "ast",    label: "APG",  season: "ast",    recent: "recent_ast",    isPct: false, isMin: false },
  { key: "fg_pct", label: "FG%",  season: "fg_pct", recent: "recent_fg_pct", isPct: true,  isMin: false },
  { key: "fg3_pct",label: "3P%",  season: "fg3_pct",recent: "recent_fg3_pct",isPct: true,  isMin: false },
];

const MOVER_ICON_MAP = {
  pts: Zap, min: Clock3, reb: Shield, ast: Users, fg_pct: Target, fg3_pct: Crosshair,
};

const LEAGUES = ["ALL", "NBA", "NCAA", "WNCAA", "WNBA", "PRO"];
const LEAGUE_LABEL = { ALL: "All Players", NBA: "NBA", NCAA: "NCAA M", WNCAA: "NCAA W", WNBA: "WNBA", PRO: "PRO" };

export default function TrendingPage() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [averages, setAverages] = useState({});
  const [trending, setTrending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { players: list, averages: map } = await api.playersSummary();
      setPlayers(list);
      setAverages(map || {});
      api.trending(5).then(setTrending).catch(() => setTrending(null));
    } catch (e) {
      toast.error("Failed to load", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Per-stat movers — for each stat, compute % change of last-5 vs season avg per player,
  // then take top-3 gainers and bottom-3 decliners.
  const perStatMovers = useMemo(() => {
    const byId = trending?.by_id || {};
    const scoped = league === "ALL" ? players : players.filter(p => p.league === league);
    return STATS.map(cfg => {
      const items = scoped.map(p => {
        const a = averages[p.id]; const t = byId[p.id];
        if (!a || !t) return null;
        const seasonRaw = a[cfg.season];
        const recentRaw = t[cfg.recent];
        if (recentRaw == null) return null;
        const season = cfg.isMin ? minToNumber(seasonRaw) : Number(seasonRaw);
        const recent = Number(recentRaw);
        if (!Number.isFinite(season) || !Number.isFinite(recent) || season <= 0) return null;
        // For percentage stats, show absolute percentage-point delta (e.g., 40% -> 50% = +10 pts).
        // For counting stats, show relative % change (e.g., 20 PPG -> 24 PPG = +20%).
        const pctChange = cfg.isPct ? ((recent - season) * 100) : (((recent - season) / season) * 100);
        return {
          id: p.id, name: p.name, team: p.team || "", league: p.league,
          has_uploaded_headshot: p.has_uploaded_headshot,
          nba_official_id: p.nba_official_id,
          season, recent, pctChange,
        };
      }).filter(Boolean);
      const up = items.filter(i => i.pctChange > 0).sort((a, b) => b.pctChange - a.pctChange).slice(0, 3);
      const down = items.filter(i => i.pctChange < 0).sort((a, b) => a.pctChange - b.pctChange).slice(0, 3);
      return { ...cfg, up, down };
    });
  }, [players, averages, trending, league]);

  const anyMovers = perStatMovers.some(m => m.up.length + m.down.length > 0);

  const kpis = useMemo(() => {
    const rows = players.map(p => averages[p.id]).filter(Boolean);
    const games = rows.reduce((s, r) => s + (Number(r.games_played) || 0), 0);
    return { count: players.length, games };
  }, [players, averages]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="border-b border-white/10" data-testid="trending-header">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-4 sm:px-6 lg:px-10 py-8 space-y-6">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl leading-none tracking-tight text-white uppercase" data-testid="trending-title">
              Trending Leaderboard
            </h1>
            <div className="h-[2px] w-16 bg-[var(--org-accent)] mt-1" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#757575] mt-3">
              Last 5 games vs season · Top 3 gainers and 3 decliners per stat
            </div>
          </div>
          <div className="flex flex-wrap items-stretch justify-between gap-6">
            <div className="flex items-end gap-10 md:gap-14">
              <Kpi label="Players Tracked" value={kpis.count} />
              <Kpi label="Games Logged"   value={kpis.games} />
            </div>
            <div className="flex flex-wrap gap-3">
              <PageNavCard variant="player-tracker" to="/" players={players} />
              <PageNavCard variant="trending" active />
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-4 sm:px-6 lg:px-10 py-8">
        {/* League toggle — filters every mover card in one place */}
        <div className="flex items-center gap-3 mb-6" data-testid="trending-league-toggle">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#757575] mr-1">Filter</span>
          {LEAGUES.map(l => (
            <button
              key={l}
              onClick={() => setLeague(l)}
              data-testid={`trending-tab-${l.toLowerCase()}`}
              className={`px-4 h-10 text-[11px] uppercase tracking-[0.25em] font-bold border ${
                league === l
                  ? "bg-[var(--org-accent)] text-[#121212] border-[var(--org-accent)]"
                  : "border-white/10 text-[#a0a0a0] hover:text-white hover:border-[var(--org-accent)]"
              }`}
              style={{ transition: "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease" }}
            >
              {LEAGUE_LABEL[l] || l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {STATS.map(s => <div key={s.key} className="h-56 border border-white/10 bg-[#1c1c1c] animate-pulse" />)}
          </div>
        ) : !trending || !anyMovers ? (
          <div className="border border-dashed border-white/15 bg-[#1c1c1c]/50 p-12 text-center" data-testid="trending-empty">
            <div className="font-heading text-2xl text-white uppercase mb-2">Not enough data yet</div>
            <div className="text-sm text-[#a0a0a0] max-w-md mx-auto">
              Trends need at least 10 games played per player so we can compare their most-recent 5 to the previous 5.
              Come back once your roster has more game logs on the books.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="per-stat-movers">
            {perStatMovers.map(m => <StatMoverCard key={m.key} mover={m} onPick={(id) => navigate(`/home/${id}`)} />)}
          </div>
        )}
      </main>
    </div>
  );
}


function Kpi({ label, value }) {
  return (
    <div>
      <div className="font-heading text-5xl text-[var(--org-accent)] stat-glow leading-none">{value}</div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#a0a0a0] mt-2">{label}</div>
    </div>
  );
}

function StatMoverCard({ mover, onPick }) {
  const Icon = MOVER_ICON_MAP[mover.key] || Zap;
  return (
    <div className="border border-white/10 bg-[#1c1c1c] p-4" data-testid={`mover-${mover.key}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[var(--org-accent)]" />
          <div className="font-heading text-xl text-[var(--org-accent)] tracking-wider">{mover.label}</div>
        </div>
        <div className="text-[9px] uppercase tracking-[0.25em] text-[#757575]">Last 5 vs Season</div>
      </div>
      <MoverList direction="up" items={mover.up} isPct={mover.isPct} isMin={mover.isMin} onPick={onPick} />
      <div className="my-2 border-t border-white/5" />
      <MoverList direction="down" items={mover.down} isPct={mover.isPct} isMin={mover.isMin} onPick={onPick} />
    </div>
  );
}

function MoverList({ direction, items, isPct, isMin, onPick }) {
  const isUp = direction === "up";
  const Icon = isUp ? TrendingUp : TrendingDown;
  const color = isUp ? "text-[#4ade80]" : "text-[#f87171]";
  const bar = isUp ? "bg-[#4ade80]" : "bg-[#f87171]";
  if (!items.length) {
    return (
      <div className="flex items-center gap-2 py-1.5 text-[10px] tracking-widest uppercase text-[#575757]">
        <Icon className={`w-3 h-3 ${color}`} /> No movers
      </div>
    );
  }
  const maxAbs = Math.max(...items.map(i => Math.abs(i.pctChange)), 1);
  return (
    <div className="space-y-1.5">
      {items.map(i => {
        const pct = i.pctChange;
        const width = Math.min(100, (Math.abs(pct) / maxAbs) * 100);
        const recentDisplay = isPct ? `${(i.recent * 100).toFixed(1)}%` : i.recent.toFixed(1);
        const seasonDisplay = isPct ? `${(i.season * 100).toFixed(1)}%` : i.season.toFixed(1);
        const hasPhoto = i.has_uploaded_headshot || i.nba_official_id;
        const photoUrl = hasPhoto ? `${BACKEND_URL}/api/players/${i.id}/headshot` : null;
        const initials = (i.name || "?").split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
        return (
          <button
            key={i.id}
            onClick={() => onPick(i.id)}
            className="w-full grid grid-cols-[14px_28px_1fr_auto] items-center gap-2 py-1 hover:bg-white/[0.02] px-1 -mx-1"
            style={{ transition: "background-color 0.15s ease" }}
            title={`Last 5: ${recentDisplay} · Season: ${seasonDisplay}`}
            data-testid={`mover-row-${i.id}`}
          >
            <Icon className={`w-3.5 h-3.5 ${color}`} />
            <div className="w-7 h-7 border border-white/10 bg-[#101010] flex items-center justify-center overflow-hidden shrink-0">
              {photoUrl
                ? <img src={photoUrl} alt="" className="w-full h-full object-cover object-top" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                : <span className="text-[9px] font-bold text-[var(--org-accent)]">{initials}</span>}
            </div>
            <div className="min-w-0 text-left">
              <div className="flex items-baseline gap-2">
                <div className="text-xs text-white truncate">{i.name}</div>
                {i.team && <div className="text-[10px] text-[#a0a0a0] truncate hidden sm:block">{i.team}</div>}
              </div>
              <div className="h-0.5 mt-1 bg-white/5 relative overflow-hidden">
                <div className={`absolute inset-y-0 left-0 ${bar}/70`} style={{ width: `${width}%` }} />
              </div>
            </div>
            <div className={`font-mono text-xs font-bold ${color} tabular-nums`}>
              {pct >= 0 ? "+" : ""}{pct.toFixed(1)}{isPct ? " pts" : "%"}
            </div>
          </button>
        );
      })}
    </div>
  );
}
